/**
 * The account hint on an onboarding email's link, and what the app does with it (#5895).
 *
 * What happened to the customer who reported it: she paid on the web under her gmail
 * address. Earlier that morning a Google sign-in on her phone had made a second, empty
 * account under her work address. The ActiveCampaign onboarding email went, correctly,
 * to the gmail address — but its link opened the plain sign-in page, which knew nothing
 * about which account the email was about. She pressed "Sign in with Google", Google
 * picked the work identity, and she landed on the empty account's Subscribe page and
 * started four checkouts, believing she was being billed twice.
 *
 * The #4936 guard (`new-social-account-guard.ts`) could not catch it: it speaks only when
 * a social sign-in CREATES an account, and this one already existed.
 *
 * So the link now carries the address it was sent to, as `?account=<email>`. That is a
 * HINT, not a credential: it proves nothing and grants nothing. When the account the
 * person ends up in has a different address, the app says so and offers to email a
 * sign-in link to the hinted address. The link always goes to that inbox, never to the
 * person clicking, so a crafted hint cannot give anybody access to anything.
 *
 * There is NO Angular and NO Firebase in this file, so the unit-test runner can load it,
 * the same split `new-social-account-guard.ts` and `deep-link-signin.ts` use. The wiring
 * lives in `account-hint.service.ts`. The design and its trade-offs are written up in
 * design-docs/purchase-deep-linking.md, "The account hint on onboarding links (#5895)".
 */

import { normalizeEmail } from '@cf-shared/email-normalization';
import { isSameAccountAsSignedIn, shouldOfferExistingAccountRecovery } from './new-social-account-guard';

/**
 * The query parameter the onboarding link carries the address in.
 *
 * MIRRORED in the ActiveCampaign onboarding emails, which are edited in ActiveCampaign and
 * not in this repository: their app links end in `?account=%EMAIL%`, for example
 * `https://www.mh-physique.app/intake-form?account=%EMAIL%`. The hint is read on any page.
 * Rename this and every email already in somebody's inbox stops working.
 */
export const ACCOUNT_HINT_PARAM = 'account';

/**
 * The longest hint accepted. A real address is rarely past 60 characters, and this is
 * text we show inside our own dialog, so a long value is refused rather than shown.
 */
const MAX_HINT_LENGTH = 100;

/**
 * The shape an accepted hint must have. Deliberately tighter than what an email address
 * may legally contain.
 *
 * The hint arrives from a link anybody can write, and it is shown inside our own dialog.
 * The legal local-part characters include `/`, `?`, `=` and `%`, which let a crafted
 * address read like an instruction (`visit-evil.com/help@x.co`). Addresses people really
 * use fit in letters, digits, `.`, `_`, `+` and `-`, and refusing anything else costs
 * nothing: a refused hint leaves the app behaving exactly as it did before hints existed.
 * A literal ActiveCampaign tag that was never filled in (`%EMAIL%`) fails this too.
 */
const ACCEPTED_HINT_SHAPE = /^[a-z0-9._+-]{1,64}@[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/** An ActiveCampaign personalisation tag that reached us unfilled, such as `%EMAIL%`. */
const UNFILLED_TAG_SHAPE = /%[a-z_]+%/i;

/** Why a hint was refused. Reported to Sentry by reason and length only, never by value. */
export enum AccountHintRejection {
  /** The email tool sent its placeholder instead of the address. */
  UnfilledTag = 'unfilled-tag',
  /** Longer than any address we accept. */
  TooLong = 'too-long',
  /** Not shaped like an address we accept. */
  BadShape = 'bad-shape',
}

/** What a hint in a link turned out to be. */
export type AccountHintParse =
  /** The link carried no hint. */
  | { kind: 'absent' }
  /** A usable address, in the canonical form accounts are compared in. */
  | { kind: 'valid'; email: string }
  /** Something was there, but it is not an address we will use or show. */
  | { kind: 'malformed'; reason: AccountHintRejection };

/**
 * Turns the raw parameter value into an address, or says why it will not be used.
 *
 * A space is turned back into `+`. Query strings decode `+` as a space, so if the email
 * tool puts `jane+fit@gmail.com` into the link without encoding it, it arrives as
 * `jane fit@gmail.com`. A real address never contains a space, so this repair cannot
 * damage one.
 */
export function parseAccountHint(raw: string | null | undefined): AccountHintParse {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return { kind: 'absent' };
  if (UNFILLED_TAG_SHAPE.test(trimmed)) return { kind: 'malformed', reason: AccountHintRejection.UnfilledTag };
  if (trimmed.length > MAX_HINT_LENGTH) return { kind: 'malformed', reason: AccountHintRejection.TooLong };

  const email = normalizeEmail(trimmed.replace(/ /g, '+'));
  if (!ACCEPTED_HINT_SHAPE.test(email)) return { kind: 'malformed', reason: AccountHintRejection.BadShape };
  return { kind: 'valid', email };
}

/** Reads the hint out of a whole link, for the native deep-link path. */
export function readAccountHintFromUrl(url: URL): AccountHintParse {
  return parseAccountHint(url.searchParams.get(ACCOUNT_HINT_PARAM));
}

/** How the signed-in account compares with the hint. */
export enum HintComparison {
  /** No hint to compare. */
  NoHint = 'no_hint',
  /** The signed-in account is the one the link named. */
  Match = 'match',
  /** The signed-in account is a different one. */
  Mismatch = 'mismatch',
  /**
   * The signed-in account has no email address at all (Sign in with Apple can do this).
   * We cannot tell whether it is the right account, and a dialog could not say which
   * account they are in, so this never produces a question.
   */
  NoSignedInEmail = 'no_signed_in_email',
}

/**
 * Compares the account somebody is in with the account the link named.
 *
 * Uses the same exact comparison as the #4936 guard (`isSameAccountAsSignedIn`), never the
 * fuzzy matcher that treats Gmail dot and `+tag` variants as one person. Those variants
 * can be separate Firebase accounts, and they are exactly the case this exists to catch.
 */
export function compareWithHint(accountHint: string | null, signedInEmail: string | null | undefined): HintComparison {
  if (!accountHint) return HintComparison.NoHint;
  if (!normalizeEmail(signedInEmail ?? undefined)) return HintComparison.NoSignedInEmail;
  return isSameAccountAsSignedIn(accountHint, signedInEmail) ? HintComparison.Match : HintComparison.Mismatch;
}

/** Which question, if any, to ask right after a sign-in on the login page. */
export enum PostSignInOffer {
  None = 'none',
  /** Tell them the link named another account, and offer to email that account a link. */
  AccountHintMismatch = 'account-hint-mismatch',
  /** The #4936 question: signing in made a new account, do you have another one? */
  ExistingAccountRecovery = 'existing-account-recovery',
}

/** What the app knows about a sign-in on the login page that has just finished. */
export interface PostSignInContext {
  /** The address the link named, or null when there is none. */
  accountHint: string | null;
  /** The address of the account they are now in, or null when it has none. */
  signedInEmail: string | null;
  /** True when they pressed "Sign in", false when they pressed "Register". */
  userPressedSignIn: boolean;
  /** True when this sign-in CREATED the account. Always false for a password sign-in. */
  accountWasJustCreated: boolean;
}

/**
 * Picks the one question to ask after a sign-in, when both could apply.
 *
 * A mismatch wins over the #4936 question because it knows more: the #4936 question has
 * to ask which address they bought under, while here we already know it.
 *
 * A hint that MATCHES, or no hint, changes nothing and the #4936 rule applies unchanged.
 * That is load-bearing, not tidiness. The address an email was sent to is normally the
 * account's address, but not always — somebody who signed in with Apple's "Hide My Email"
 * has an account under a relay address while the email tool knows their real one. For
 * them "the hint matches and a new account was just made" is exactly the #4936 case, and
 * letting the hint silence it would bring that bug back.
 */
export function decidePostSignInOffer(ctx: PostSignInContext): PostSignInOffer {
  if (compareWithHint(ctx.accountHint, ctx.signedInEmail) === HintComparison.Mismatch) {
    return PostSignInOffer.AccountHintMismatch;
  }
  if (shouldOfferExistingAccountRecovery(ctx)) return PostSignInOffer.ExistingAccountRecovery;
  return PostSignInOffer.None;
}

/** What the app knows when somebody who is ALREADY signed in opens a link with a hint. */
export interface SignedInArrivalContext {
  /** The address the link named, or null when there is none. */
  accountHint: string | null;
  /** The address of the account they are in, or null when it has none. */
  signedInEmail: string | null;
  /**
   * True when another link is already being dealt with: a sign-in link waiting for its
   * "switch account?" answer, a coach invitation, or a checkout that has just finished.
   * Each of those is more specific than a hint and has its own dialog, and two questions
   * about accounts on top of each other would only confuse.
   */
  otherLinkFlowPending: boolean;
  /**
   * True when the person at the keyboard is a coach managing a client, or an admin
   * viewing somebody's account. The account shown is then not theirs, so comparing it
   * with an email meant for them would be wrong.
   */
  actingForSomeoneElse: boolean;
}

/** Whether to ask anything when an already signed-in person opens a link with a hint. */
export function shouldAskOnSignedInArrival(ctx: SignedInArrivalContext): boolean {
  if (ctx.otherLinkFlowPending || ctx.actingForSomeoneElse) return false;
  return compareWithHint(ctx.accountHint, ctx.signedInEmail) === HintComparison.Mismatch;
}

/**
 * Holds the hint for the life of this page, and hands it out ONCE.
 *
 * Two places can act on a hint — the login page right after a sign-in, and the boot once
 * a signed-in app has loaded — and whichever comes first must be the only one. `consume`
 * is what guarantees that. It lives in memory only: a hint is personal data, and nothing
 * needs it after this page load.
 */
export class AccountHintHolder {
  private hint: string | null = null;

  /** Keep this address as the hint, replacing any earlier one. */
  remember(email: string) {
    this.hint = email;
  }

  /** The hint, without using it up. For pre-filling a field or preselecting an account. */
  peek(): string | null {
    return this.hint;
  }

  /** The hint, which is then gone, so no second place can act on it. */
  consume(): string | null {
    const hint = this.hint;
    this.hint = null;
    return hint;
  }
}
